/*
 * <eu-spotlight> — hands the cursor's position to one button's CSS.
 * Used by eu-hero. No dependencies.
 *
 * While a fine pointer is within `data-field` px of the wrapped element it
 * writes, on that element:
 *   --eu-mx / --eu-my  the pointer's position within the element's box, in
 *                      px (outside the box while it is only near);
 *   --eu-lit           nearness, 0 at the edge of the field to 1 over it.
 * All three are removed when the pointer leaves the field. Nothing visible
 * lives here; the CSS draws from the values.
 *
 * Markup contract:
 *   <eu-spotlight data-field="80"><a>…</a></eu-spotlight>
 * The wrapper draws no box, so it listens on its parent, which must extend
 * past the button by at least the field.
 *
 * Coarse pointers get nothing.
 */
(() => {
  if (customElements.get('eu-spotlight')) return;

  const FINE = window.matchMedia('(hover: hover) and (pointer: fine)');

  class EuSpotlight extends HTMLElement {
    connectedCallback() {
      this.target = this.firstElementChild;
      this.field = this.parentElement;
      if (!this.target || !this.field || !FINE.matches) return;

      this.radius = parseFloat(this.dataset.field) || 80;
      this.onMove = this.onMove.bind(this);
      this.release = this.release.bind(this);
      this.field.addEventListener('pointermove', this.onMove);
      this.field.addEventListener('pointerleave', this.release);
    }

    disconnectedCallback() {
      this.field?.removeEventListener('pointermove', this.onMove);
      this.field?.removeEventListener('pointerleave', this.release);
    }

    onMove(e) {
      const t = this.target;
      const r = t.getBoundingClientRect();
      const x = e.clientX - r.left;
      const y = e.clientY - r.top;
      // Distance from the pointer to the nearest edge of the button.
      const gap = Math.hypot(
        Math.max(-x, x - r.width, 0),
        Math.max(-y, y - r.height, 0)
      );
      if (gap >= this.radius) {
        this.release();
        return;
      }

      const near = 1 - gap / this.radius;
      const strength = near * near * (3 - 2 * near); // smoothstep: no kink at either end
      t.style.setProperty('--eu-mx', `${x.toFixed(1)}px`);
      t.style.setProperty('--eu-my', `${y.toFixed(1)}px`);
      t.style.setProperty('--eu-lit', strength.toFixed(3));
    }

    release() {
      const t = this.target;
      t.style.removeProperty('--eu-mx');
      t.style.removeProperty('--eu-my');
      t.style.removeProperty('--eu-lit');
    }
  }

  customElements.define('eu-spotlight', EuSpotlight);
})();
